import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, signInWithEmail, signUpWithEmail, signOutUser } from '../firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import config from '../config/app';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);

  useEffect(() => {
    // Check for existing session token
    const token = localStorage.getItem('sessionToken');
    if (token) {
      setSessionToken(token);
      verifySession(token);
    }

    // Listen for Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser && !sessionToken) {
        // User signed in with Firebase, verify with backend
        try {
          const idToken = await firebaseUser.getIdToken();
          await loginWithBackend(idToken, firebaseUser);
        } catch (error) {
          console.error('Backend authentication failed:', error);
          await signOutUser();
        }
      } else if (!firebaseUser && sessionToken) {
        // User signed out from Firebase
        logout();
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [sessionToken]);

  const verifySession = async (token) => {
    try {
      const response = await fetch(`${config.apiBasePath}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // Session invalid, clear storage
        localStorage.removeItem('sessionToken');
        setSessionToken(null);
      }
    } catch (error) {
      console.error('Session verification failed:', error);
      localStorage.removeItem('sessionToken');
      setSessionToken(null);
    }
  };

  const loginWithBackend = async (firebaseIdToken, firebaseUser) => {
    try {
      const response = await fetch(`${config.apiBasePath}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken: firebaseIdToken
        })
      });

      if (response.ok) {
        const data = await response.json();
        const { sessionToken: newSessionToken, user: userData } = data;
        
        localStorage.setItem('sessionToken', newSessionToken);
        setSessionToken(newSessionToken);
        setUser(userData);
        
        return userData;
      } else {
        const error = await response.json();
        throw new Error(error.detail || error.message || 'Authentication failed');
      }
    } catch (error) {
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const { user: firebaseUser, idToken } = await signInWithEmail(email, password);
      
      // Backend will handle the rest via onAuthStateChanged
      return firebaseUser;
    } catch (error) {
      setIsLoading(false);
      if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email address');
      } else if (error.code === 'auth/wrong-password') {
        throw new Error('Incorrect password');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address');
      } else if (error.code === 'auth/user-disabled') {
        throw new Error('This account has been disabled');
      } else {
        throw new Error(error.message || 'Sign-in failed');
      }
    }
  };

  const signUp = async (email, password) => {
    try {
      setIsLoading(true);
      const { user: firebaseUser, idToken } = await signUpWithEmail(email, password);
      
      // Backend will handle the rest via onAuthStateChanged
      return firebaseUser;
    } catch (error) {
      setIsLoading(false);
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('An account with this email already exists');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Password should be at least 6 characters');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address');
      } else {
        throw new Error(error.message || 'Sign-up failed');
      }
    }
  };

  const logout = async () => {
    try {
      // Clear session token
      localStorage.removeItem('sessionToken');
      setSessionToken(null);
      setUser(null);
      
      // Sign out from Firebase
      await signOutUser();
      
      // Notify backend of logout (optional)
      if (sessionToken) {
        fetch(`${config.apiBasePath}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        }).catch(() => {}); // Ignore errors on logout
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    user,
    isLoading,
    login,
    signUp,
    logout,
    isAuthenticated: !!user && !!sessionToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
